import React from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

/**
 * Hides progress photo UI until the coach explicitly reveals it
 * (reduces accidental on-screen exposure / shoulder surfing).
 * Resets to hidden when resetKey changes (e.g. client or report id).
 */
const ProgressPhotosPrivacyGate = ({
  resetKey,
  children,
  title = "Progress photos are hidden",
  description = "Reveal only when you intend to review them — helpful if someone is nearby.",
  revealLabel = "Reveal progress photos",
  hideLabel = "Hide progress photos",
  showHideButton = true,
}) => {
  const [revealed, setRevealed] = React.useState(false);

  React.useEffect(() => {
    setRevealed(false);
  }, [resetKey]);

  if (!revealed) {
    return (
      <Box
        sx={{
          py: 4,
          px: 3,
          textAlign: "center",
          backgroundColor: "grey.50",
          border: "2px dashed",
          borderColor: "grey.300",
          borderRadius: (theme) => theme.spacing(2),
        }}
      >
        <Stack spacing={2} alignItems="center">
          <VisibilityOffIcon
            color="action"
            sx={{ fontSize: 40 }}
            aria-hidden
          />
          <Box>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: "auto" }}>
              {description}
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<VisibilityIcon />}
            onClick={() => setRevealed(true)}
            sx={{
              borderRadius: 20,
              px: 2.5,
              py: 1,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {revealLabel}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      {showHideButton ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            startIcon={<VisibilityOffIcon />}
            onClick={() => setRevealed(false)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {hideLabel}
          </Button>
        </Stack>
      ) : null}
      {children}
    </Box>
  );
};

export default ProgressPhotosPrivacyGate;
